import React, { useState, useEffect, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { Grid } from "@material-ui/core";

import {
  GridFilterModel,
  GridValueGetterParams,
  GridValueFormatterParams,
  GridPaginationModel,
  GridSortModel,
  GridRenderCellParams,
} from "@mui/x-data-grid-pro";

import {
  tableCellRender,
  formatDateValue,
  dateFilterOperators,
  stringFilterOperators,
  isSingleSelectOperators,
  getLatestUpdateDaily,
  getCompanyCodeName,
  buildingCompanyColumnFeatures,
} from "../../util/tableUtils";
import { DataGridCystellarTable } from "../../util/DataGridCystellarTable";

import { goToClaimPage } from "./../../claim/utils";
import httpClient from "../../../lib/httpClient";
import { apiError } from "../../../lib/apiError";
import { initialFilterModel, isThereAnyIncompleteFilter } from "../../util/tableUtilsTS";

import { getUserPermissionsList } from "../../util/permissionsUtils";
import { DownloadTableButton } from "../../util/DownloadTableButton";
import { getFilterQuery, getSortDetailsString } from "../../emergencyMonitoring/emergencyUtils";
import { useClientConfig } from "../../../context/clientConfigContext";
import { PermissionIdEnum } from "../../util/types/permissions/permissionIdEnum";

interface EmergencyClaimsProps {
  activationCode: string;
}

interface RowData {
  _id: string;

  row: { claimCode: string; companyCode: string; year: string; perilCode: string; perilGroup: string };
  // Other fields are not required by typecheck.
}

export const EmergencyClaims: React.FC<EmergencyClaimsProps> = ({ activationCode }) => {
  const { t } = useTranslation();
  const config = useClientConfig();

  const [claimsData, setClaimsData] = useState([]);
  const [tableState, setTableState] = useState({
    isLoading: false,
    total: 0,
  });

  const [paginationModel, setPaginationModel] = useState({
    page: 0,
    pageSize: 10,
  });
  const [filterModel, setFilterModel] = useState<GridFilterModel>(initialFilterModel);
  const [sortModel, setSortModel] = useState<GridSortModel>([]);

  const [lastUpdateDateMsg, setLastUpdateDateMsg] = useState("");

  useEffect(() => {
    getLatestUpdateDaily("/serviceLogs/buildings?service=db_claims_daily").then((lastUpdateMsg) => {
      setLastUpdateDateMsg(lastUpdateMsg);
    });
  }, []);

  const getClaimsData = useCallback(async () => {
    setTableState((old) => ({ ...old, isLoading: true }));

    try {
      const { data: claimsResponse } = await httpClient().get(
        `/terrashield/terraShieldClaims?page=${paginationModel.page}&pageSize=${paginationModel.pageSize}&filters=${getFilterQuery(filterModel, activationCode)}&${getSortDetailsString(sortModel)}`,
      );
      setClaimsData(claimsResponse.data);
      setTableState((old) => ({
        ...old,
        isLoading: false,
        total: claimsResponse.total,
      }));
    } catch (error) {
      apiError(error);

      setClaimsData([]);
      setTableState((old) => ({
        ...old,
        isLoading: false,
        total: 0,
      }));
    }
  }, [filterModel, sortModel, paginationModel, activationCode]);

  useEffect(() => {
    if (!isThereAnyIncompleteFilter(filterModel)) {
      getClaimsData();
    }
  }, [getClaimsData, filterModel]);

  const downloadEmergencyClaimsTableFunction = async () => {
    try {
      const { data: claimsResponse } = await httpClient().get(
        `/terrashield/terraShieldClaims?page=0&pageSize=9999999&filters=${getFilterQuery(filterModel, activationCode)}&${getSortDetailsString(sortModel)}`,
      );

      return claimsResponse.data;
    } catch (error) {
      apiError(error);
      return [];
    }
  };

  const onFilterChange = useCallback((aFilterModel) => {
    setFilterModel(aFilterModel);
  }, []);

  const onSortChange = useCallback((aSortModel) => {
    setSortModel(aSortModel);
  }, []);

  const claimsColumns = [
    {
      field: "companyCode",
      headerName: t("company"),
      ...buildingCompanyColumnFeatures,
      valueGetter: (params: GridValueGetterParams) =>
        getCompanyCodeName(params.row.companyCode, config.companyCodesMap),
    },
    {
      field: "claimCode",
      headerName: t("claims.claimCode"),
      width: 170,
    },

    {
      field: "belongsToFloodArea",
      headerName: "Flood",
      sortable: true,
      width: 70,
      renderCell: (params: GridRenderCellParams) => {
        return <span style={{ color: params.value === "TRUE" ? "red" : "blue" }}>{params.value}</span>;
      },

      type: "singleSelect",
      filterOperators: isSingleSelectOperators,
      valueOptions: [
        { value: "TRUE", label: "TRUE" },
        { value: "FALSE", label: "FALSE" },
      ],
    },

    {
      field: "policyNumberSecondary",
      headerName: t("policy.id"),
      width: 130,
    },
    {
      field: "referenceDate",
      headerName: t("coverage.year"),
      width: 70,
    },
    {
      field: "claimAddressCity",
      headerName: t("city"),
      width: 130,
    },
    {
      field: "claimAddressCityCode",
      headerName: t("cityCode"),
      width: 70,
    },
    {
      field: "claimAddressStreetName",
      headerName: t("address"),
      width: 150,
      valueGetter: (params: GridValueGetterParams) =>
        `${params.row.claimAddressType ? params.row.claimAddressType : ""} ${
          params.row.claimAddressStreetName ? params.row.claimAddressStreetName : ""
        } ${params.row.claimAddressBuildingNo ? params.row.claimAddressBuildingNo : ""}`,
    },
    {
      field: "claimAddressZIPCode",
      headerName: t("zip"),
      width: 60,
    },
    {
      field: "claimAddressProvince",
      headerName: t("state"),
      width: 50,
    },
    {
      field: "claimReportDate",
      headerName: t("claims.reportDate"),
      type: "date",
      width: 100,

      filterOperators: dateFilterOperators,
      valueGetter: (params: GridValueGetterParams) =>
        params.row.claimReportDate ? formatDateValue(params.row.claimReportDate) : "",
    },
    {
      field: "claimEventDate",
      headerName: t("claims.claimEventDate"),
      type: "date",
      width: 100,
      sortable: false,
      filterOperators: dateFilterOperators,
      valueGetter: (params: GridValueGetterParams) =>
        params.row.claimEventDate ? formatDateValue(params.row.claimEventDate) : "",
    },

    {
      field: "claimCauseDescription",
      headerName: t("claims.claimCause"),
      width: 100,
    },

    {
      field: "perilName",
      headerName: t("perilName"),
      width: 100,
    },
    {
      field: "perilGroup",
      headerName: t("perilGroup.title"),
      width: 100,
    },
  ];

  const columns = claimsColumns.map((tableColumn) => ({
    valueFormatter: (params: GridValueFormatterParams) => tableCellRender(params.value?.toString()),
    filterOperators: stringFilterOperators,

    ...tableColumn,
  }));

  return (
    <Grid item xs={12} sm={12}>
      <DataGridCystellarTable
        columns={columns}
        rows={claimsData}
        rowCount={tableState.total}
        getRowId={(rowData: RowData) => rowData._id}
        onRowClick={(rowData: RowData) => goToClaimPage(rowData.row)}
        loading={tableState.isLoading}
        paginationMode={"server"}
        paginationModel={paginationModel}
        pageSizeOptions={[10, 20, 50]}
        onPaginationModelChange={(newPaginationModel: GridPaginationModel) =>
          setPaginationModel(newPaginationModel)
        }
        filterMode={"server"}
        onFilterModelChange={onFilterChange}
        sortingMode={"server"}
        onSortModelChange={onSortChange}
        customToolbarComp={lastUpdateDateMsg ? true : false}
        customToolbarCompMsg={lastUpdateDateMsg}
        customToolbarWidgets={
          <>
            {getUserPermissionsList().includes(PermissionIdEnum.Super) && (
              <DownloadTableButton
                downloadTableFunction={downloadEmergencyClaimsTableFunction}
                total={tableState.total}
                fileName="SinistriEmergenza"
              ></DownloadTableButton>
            )}
          </>
        }
      />
    </Grid>
  );
};
